/**
 * Sidebar SkillMap — navigation groupée (réf. SkillMap V0).
 */
import {
  BarChart2,
  Bell,
  Calendar,
  CheckSquare,
  ClipboardList,
  Database,
  Download,
  Eye,
  Home,
  Layers,
  Scale,
  Settings,
  Shield,
  Upload,
  User,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";

import { useAuth } from "../../context/AuthContext";

type NavItem = {
  to: string;
  end?: boolean;
  labelKey: string;
  icon: LucideIcon;
  minLevel?: number;
};

type NavGroup = {
  labelKey?: string;
  minLevel?: number;
  items: NavItem[];
};

function NavItemLink({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => (isActive ? "active" : undefined)}
    >
      <Icon size={18} className="sm-nav-icon" aria-hidden />
      <span>{t(item.labelKey)}</span>
    </NavLink>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const level = user?.accessLevel ?? 0;

  const groups: NavGroup[] = [
    {
      items: [
        { to: "/", end: true, labelKey: "nav_dashboard", icon: Home },
        { to: "/assessments", labelKey: "nav_assessment", icon: ClipboardList },
        { to: "/my-application-skills", labelKey: "nav_app_skills", icon: ClipboardList },
        { to: "/my-transversal-skills", labelKey: "nav_transversal_skills", icon: Layers },
        { to: "/validation", labelKey: "validation_dashboard", icon: CheckSquare, minLevel: 50 },
        { to: "/campaigns", labelKey: "campaigns", icon: Calendar, minLevel: 50 },
        { to: "/preevals", labelKey: "preevals", icon: Eye, minLevel: 50 },
      ],
    },
    {
      labelKey: "nav_analytics",
      minLevel: 10,
      items: [
        { to: "/analytics", end: true, labelKey: "analytics_hub", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/heatmap", labelKey: "heat_map", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/radar", labelKey: "radar_chart", icon: BarChart2 },
        { to: "/analytics/gaps", labelKey: "gap_analysis", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/progression", labelKey: "individual_progression", icon: BarChart2 },
        { to: "/analytics/coverage", labelKey: "team_coverage", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/training-needs", labelKey: "training_needs", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/distribution", labelKey: "distribution", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/transversal-team", labelKey: "transversal_team", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/domain-bench", labelKey: "domain_bench", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/plaque", labelKey: "plaque_view", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/rare-expertise", labelKey: "rare_expertise", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/feature-heatmap", labelKey: "feature_heatmap", icon: BarChart2, minLevel: 50 },
        { to: "/analytics/feature-gap", labelKey: "feature_gap", icon: BarChart2, minLevel: 50 },
      ],
    },
    {
      labelKey: "nav_bpml",
      minLevel: 50,
      items: [
        { to: "/bpml/import", labelKey: "bpml_import", icon: Database, minLevel: 100 },
        { to: "/bpml/enrichment", labelKey: "bpml_enrichment", icon: Database, minLevel: 50 },
        { to: "/transversal-skills", labelKey: "transversal_skills", icon: Layers, minLevel: 100 },
      ],
    },
    {
      labelKey: "nav_admin",
      minLevel: 50,
      items: [
        { to: "/users", labelKey: "user_management", icon: Users, minLevel: 100 },
        { to: "/users/import", labelKey: "import_users", icon: Upload, minLevel: 100 },
        { to: "/config/referentials", labelKey: "referentials", icon: Settings, minLevel: 100 },
        { to: "/config/application-features", labelKey: "application_features", icon: Settings, minLevel: 50 },
        { to: "/config/applications-scale", labelKey: "erp_scale", icon: Scale, minLevel: 100 },
        { to: "/config/notifications", labelKey: "nav_notifications", icon: Bell, minLevel: 100 },
        { to: "/export", labelKey: "nav_export", icon: Download, minLevel: 50 },
        { to: "/audit-log", labelKey: "nav_audit", icon: Shield, minLevel: 100 },
        { to: "/profile", labelKey: "nav_profile", icon: User },
      ],
    },
  ];

  const roleLabel =
    level >= 100 ? t("role_admin") : level >= 50 ? t("role_manager") : t("role_expert");

  const roleClass =
    level >= 100 ? "sm-badge sm-badge-admin" : level >= 50 ? "sm-badge sm-badge-manager" : "sm-badge sm-badge-user";

  return (
    <aside className="sm-sidebar">
      <div className="sm-sidebar-brand">
        <div className="sm-sidebar-brand-title">{t("app_name")}</div>
        <div className="sm-sidebar-brand-sub">{t("tagline")}</div>
      </div>

      <nav className="sm-sidebar-nav">
        {groups.map((group, gi) => {
          if (group.minLevel != null && level < group.minLevel) return null;
          const items = group.items.filter((i) => (i.minLevel ?? 0) <= level);
          if (items.length === 0) return null;
          return (
            <div key={gi} className="sm-nav-group">
              {group.labelKey ? <div className="sm-nav-label">{t(group.labelKey)}</div> : null}
              {items.map((item) => (
                <NavItemLink key={item.to} item={item} />
              ))}
            </div>
          );
        })}
      </nav>

      <div className="sm-sidebar-footer">
        {user ? (
          <>
            <div className="name">{user.fullName}</div>
            <div className="meta">{user.email}</div>
            <span className={roleClass}>{roleLabel}</span>
          </>
        ) : null}
      </div>
    </aside>
  );
}
